// First import on purpose - see the header comment in polyfills.js. Webpack hoists every ESM
// import above the require() below, so this is what actually runs before vue and @inertiajs/vue3.
import './polyfills';

require('./bootstrap');

import { createApp, h } from 'vue';
import { createInertiaApp, router } from '@inertiajs/vue3';
import { i18nVue } from 'laravel-vue-i18n'
import * as Sentry from '@sentry/vue';

const appName = window.document.getElementsByTagName('title')[0]?.innerText || 'Laravel';

/*
 * Error monitoring. The config block is rendered by resources/views/app.blade.php and is absent
 * whenever SENTRY_LARAVEL_DSN is empty, which switches all of this off without a rebuild.
 *
 * Initialising here rather than inside setup() means window.onerror and unhandledrejection are
 * covered from the moment the bundle evaluates; the Vue error handler is attached further down,
 * once createApp() has produced an instance.
 */
const monitoring = window.hqamMonitoring;

if (monitoring?.dsn) {
    Sentry.init({
        dsn: monitoring.dsn,
        environment: monitoring.environment,
        release: monitoring.release || undefined,
        // Errors only, by design (ADR-005): no browserTracing, replay or feedback integrations,
        // so no performance events are billed and the bundle stays small.
        sampleRate: 1.0,
        sendDefaultPii: false,
        // The Vue app does not exist yet (see above), so @sentry/vue's own default Vue integration
        // would find neither `app` nor `Vue`, warn "Misconfigured SDK. Vue specific errors will not
        // be captured." into every production console and then return without doing anything. Vue's
        // error handler is attached by hand in setup() below - the same call that integration makes.
        integrations: (defaultIntegrations) =>
            defaultIntegrations.filter((integration) => integration.name !== 'Vue'),
    });
}

/*
 * Vue component props are NOT reported: on this portal they carry customer names, document
 * metadata and onboarding answers, which must not leave the app (ADR-005).
 */
const sentryVueOptions = { attachProps: false };

const syncSentryUser = (pageProps) => {
    if (!monitoring?.dsn) {
        return;
    }

    const userId = pageProps?.sentry?.user_id ?? null;

    Sentry.setUser(userId ? { id: userId } : null);
};

router.on('success', (event) => syncSentryUser(event.detail.page.props));

createInertiaApp({
    title: (title) => `${title} - ${appName}`,
    progress: {
        color: '#4B5563',
    },
    resolve: (name) => require(`./Pages/${name}.vue`),
    setup({ el, App, props, plugin }) {
        const app = createApp({ render: () => h(App, props) });

        // Must happen before the i18nVue plugin below: its onLoad callback is what mounts the app,
        // and attaching afterwards would miss any error thrown during the first render.
        if (monitoring?.dsn) {
            Sentry.attachErrorHandler(app, sentryVueOptions);
            syncSentryUser(props.initialPage.props);
        }

        app.use(i18nVue, {
                resolve: lang => import(`../../lang/${lang}.json`),
                onLoad: () => {
                    if (el && el.__vue_app__) /* check needed to avoid remounting (which would fail) when we call loadLanguageAsync to change language */
                        return;
                    app.mount(el);
                },
            })
            .use(plugin)
            .mixin({ methods: { route } });
    },
});
